/* eslint-disable max-lines */
import React, { FC, useEffect, useMemo, useState } from 'react'

import { useInView } from 'react-intersection-observer'
import { FormattedMessage } from 'react-intl'
import { useDispatch } from 'react-redux'
import { Route } from 'react-router-dom'

import { setImageUrlWidgetAction } from 'actions/foreignProfile/foreignProfileAction'
import { Colors } from 'common-constants/colors'
import { useMobilePhotos } from 'components/block/Profile/ProfileMain/hooks/useMobilePhotos'
import { usePhotos } from 'components/block/Profile/ProfileMain/hooks/usePhotos'
import { ForeignProfileBreadCrumbs } from 'components/block/Seo/BreadCrumbs/ForeignProfileBreadCrumbs'
import { ChatButtonPhoto } from 'components/page/ForeignProfile/ChatButtonPhoto/ChatButtonPhoto'
import { Countries } from 'components/page/ForeignProfile/Field/Countries/Countries'
import { ForeignProfileGreetingView } from 'components/page/ForeignProfile/Field/ForeignProfileGreetingView'
import { HoroscopeSignLoadable } from 'components/page/ForeignProfile/Field/HoroscopeSign/HoroscopeSignLoadable'
import { Interests } from 'components/page/ForeignProfile/Field/Interests/Interests'
import {
  StickyWrapper,
  Wrapper,
  WrapperDesktop,
  WrapperGroup,
  WrapperMobile,
  WrapperPhoto,
  InteractiveMobileBlock,
  InteractiveDesktopBlock,
} from 'components/page/ForeignProfile/ForeignProfile.styled'
import { ForeignProfileHappyBirthdayLoadable } from 'components/page/ForeignProfile/ForeignProfileHappyBirthday/ForeignProfileHappyBirthdayLoadable'
import { GQLForeignRootQuery } from 'components/page/ForeignProfile/gql/__generated__/ForeignRoot'
import { Header } from 'components/page/ForeignProfile/Header/Header'
import { InfoProfile } from 'components/page/ForeignProfile/InfoUser/InfoProfile'
import { InteractiveButtonContext } from 'components/page/ForeignProfile/InteractiveButtons/CreateContext'
import { InteractiveButtons } from 'components/page/ForeignProfile/InteractiveButtons/InteractiveButtons'
import { ForeignProfilePhotos } from 'components/page/ForeignProfile/Photos/ForeignProfilePhotos'
import { MobilePhotosIndex } from 'components/page/ForeignProfile/Photos/MobilePhotos/MobilePhotosIndex'
import { Sharing } from 'components/page/ForeignProfile/Sharing/Sharing'
import { ProfileAccommodation } from 'components/page/ProfileCommon/ProfileField/ProfileAccommodation'
import { ProfileAlcohol } from 'components/page/ProfileCommon/ProfileField/ProfileAlcohol'
import { ProfileAppearance } from 'components/page/ProfileCommon/ProfileField/ProfileAppearance'
import { ProfileChildren } from 'components/page/ProfileCommon/ProfileField/ProfileChildren'
import { ProfileConstitution } from 'components/page/ProfileCommon/ProfileField/ProfileConstitution'
import { ProfileEducation } from 'components/page/ProfileCommon/ProfileField/ProfileEducation'
import { ProfileHeight } from 'components/page/ProfileCommon/ProfileField/ProfileHeight'
import { ProfileLanguage } from 'components/page/ProfileCommon/ProfileField/ProfileLanguage'
import { ProfileLookFor } from 'components/page/ProfileCommon/ProfileField/ProfileLookFor'
import { ProfileLookForAge } from 'components/page/ProfileCommon/ProfileField/ProfileLookForAge'
import { ProfileMaterialStatus } from 'components/page/ProfileCommon/ProfileField/ProfileMaterialStatus'
import { ProfileOrientation } from 'components/page/ProfileCommon/ProfileField/ProfileOrientation'
import { ProfileSearchGoals } from 'components/page/ProfileCommon/ProfileField/ProfileSearchGoals'
import { ProfileSmoking } from 'components/page/ProfileCommon/ProfileField/ProfileSmoking'
import { ProfileWeight } from 'components/page/ProfileCommon/ProfileField/ProfileWeight'
import { PromoServiceFetchLoadable } from 'components/page/PromoService/PromoServiceFetchLoadable'
import { PromoServiceLoadable } from 'components/page/PromoService/PromoServiceLoadable'
import { SearchProfile } from 'components/page/Search/SearchProfile/SearchProfile'
import { profileSearchFullPath } from 'components/page/Search/SearchProfile/SearchProfile.paths'
import {
  ProfileContext,
  ProfileTooltipContext,
} from 'components/page/UserProfile/Context/CreateProfileContext'
import { userProfilePath } from 'components/paths'
import { DefaultLoader } from 'components/presentational/AnimatedWatch/AnimatedWatch'
import { Position } from 'components/presentational/Tooltip/Tooltip.types'
import { zIndex } from 'components/presentational/zIndex'
import { mergeAllUrls } from 'functions/mergeAllUrls'
import { tryScroll } from 'functions/tryTopScroll'
import { useAuthorized } from 'hooks/useAuthorized'
import { useLgbtDetail } from 'hooks/useLgbtDetail'
import { useLocale } from 'hooks/useLocale'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'

import { hasSectionGoal } from './function/hasSectionGoal'
import { ForeignGifts } from './Gifts/GiftsIndex'

/**
 *
 * @param data
 * Появляющиеся меню снизу на мобильных после прокрутки, не будет работать для неавторизованных пользователей
 * https://redmine.mamba.ru/issues/109422#note-6
 *
 */
export const ForeignProfileView: FC<{
  data: GQLForeignRootQuery
}> = ({ data }) => {
  const { loadingChat } = useShallowEqualSelector(
    ({ chatMessengerReducer: { loadingChat } }) => ({
      loadingChat,
    })
  )

  const dispatch = useDispatch()
  const authorized = useAuthorized()
  const locale = useLocale()
  const { lgbtModified, lgbtHidden, lgbtRestriction } = useLgbtDetail(
    data.user?.gender
  )
  const [hideButton, setHideButton] = useState<boolean>(true)

  const {
    ref: refInterButton,
    inView,
    entry,
  } = useInView({
    threshold: 0.5,
  })

  const searchPathFullUrl = useMemo(() => {
    const partSearchPathUrl = mergeAllUrls(
      userProfilePath,
      profileSearchFullPath
    )

    return authorized
      ? partSearchPathUrl
      : mergeAllUrls(locale, partSearchPathUrl)
  }, [locale, authorized])

  const { mainPhoto, secondaryPhotos, restPhotos } = usePhotos(data.user)
  const {
    mobileGoalPhoto,
    mobileInterestsPhoto,
    mobileSocialPhoto,
    mobilePhotos,
  } = useMobilePhotos(data.user)

  useEffect(() => {
    /**
     * Если наблюдаемый элемент вышел за границы,
     * viewport
     */
    if (!inView && entry && entry.boundingClientRect.y < 0) {
      setHideButton(false)
    }

    if (inView) {
      setHideButton(true)
    }
  }, [entry, inView])

  useEffect(() => {
    tryScroll()
  }, [])

  /**
   * https://redmine.mamba.ru/issues/108921
   * Делаем только на сервер и только для неавторизованных
   */
  if (
    !process.env.browser &&
    !authorized &&
    data.user?.sharing?.widgets?.thisIsMe?.url
  ) {
    dispatch(setImageUrlWidgetAction(data.user?.sharing.widgets.thisIsMe.url))
  }

  if (loadingChat) {
    return <DefaultLoader />
  }

  if (data && data.user) {
    const foreignInterests = data.user.interests.interests.nodes
    const foreignInterestsCount = data.user.interests.interestsCount

    const currentUserInterestList = data.my
      ? data.my.interests.interests.nodes
      : []

    const teamo = authorized
      ? {
          currentUser: data.my?.teamo,
          foreignUser: data.user.teamo,
        }
      : null

    return (
      <ProfileContext.Provider
        value={{
          profileId: data.user.id,
          isEditable: false,
          gender: data.user.gender,
          teamo,
          countryId: data.user.location.country.id,
        }}
      >
        <PromoServiceFetchLoadable />
        <Wrapper>
          <Header />
          <WrapperPhoto>
            <ForeignProfilePhotos
              userId={data.user.id}
              gender={data.user.gender}
              isBirthdayToday={Boolean(data.user.isBirthdayToday)}
              hasIncognito={data.user.incognito}
              mainPhoto={mainPhoto!}
              secondaryPhotos={secondaryPhotos}
              restPhotos={restPhotos}
            />
            <InteractiveMobileBlock>
              <InteractiveButtonContext.Provider
                value={{
                  backgroundColor: Colors.transparentSmallWhite,
                  id: `button_mobile_fixed_${data.user.id}`,
                }}
              >
                <div ref={refInterButton}>
                  <InteractiveButtons
                    isWinked={data.user.relations.winkedByMe!}
                    isFavourite={data.user.relations.contact?.favorite}
                    place="mobile"
                  />
                </div>
              </InteractiveButtonContext.Provider>
              <ProfileTooltipContext.Provider
                value={{
                  position: Position.TopRight,
                }}
              >
                <InfoProfile
                  name={data.user.name}
                  age={data.user.age}
                  location={data.user.location.name}
                  spaceTimeLocationRelation={
                    data.user.relations.spaceTimeLocation &&
                    data.user.relations.spaceTimeLocation.text
                  }
                  isOnline={data.user.online?.status}
                  hasVerification={data.user.verification.verifiedPhotos}
                  gender={data.user.gender}
                  nameInCenter={true}
                  data-name="profile-info-1"
                  iconUid="profile-info-1"
                />
              </ProfileTooltipContext.Provider>
              {!data.user.relations.contact?.contactId && <ChatButtonPhoto />}
            </InteractiveMobileBlock>
          </WrapperPhoto>
          <div>
            <InteractiveDesktopBlock>
              <ProfileTooltipContext.Provider
                value={{
                  position: Position.BottomCenter,
                  zIndex: zIndex.verificationTooltip,
                }}
              >
                <InfoProfile
                  name={data.user.name}
                  age={data.user.age}
                  location={data.user.location.name}
                  spaceTimeLocationRelation={
                    data.user.relations.spaceTimeLocation &&
                    data.user.relations.spaceTimeLocation.text
                  }
                  isOnline={data.user.online?.status}
                  hasVerification={data.user.verification.verifiedPhotos}
                  gender={data.user.gender}
                  data-name="profile-info-2"
                  iconUid="profile-info-2"
                />
                {data.user.isBirthdayToday && (
                  <ForeignProfileHappyBirthdayLoadable />
                )}
                <InteractiveButtonContext.Provider
                  value={{
                    backgroundColor: Colors.transparentSmallWhite,
                    id: `button_desktop_${data.user.id}`,
                  }}
                >
                  <InteractiveButtons
                    isWinked={data.user.relations.winkedByMe!}
                    isFavourite={data.user.relations.contact?.favorite}
                    place="desktop"
                  />
                </InteractiveButtonContext.Provider>
              </ProfileTooltipContext.Provider>
            </InteractiveDesktopBlock>
            {data.user.dating.aboutMe && (
              <ForeignProfileGreetingView greeting={data.user.dating.aboutMe} />
            )}
            <ForeignGifts
              gender={data.user.gender}
              gifts={data.user.gifts.gifts.nodes}
              giftsCommonFormat={data.ui.giftFormats}
              giftsCount={data.user.gifts.giftsCount}
              presentVip={data.user.vip.presentVip}
            />
            <WrapperGroup>
              {!lgbtHidden && data.user.dating.lookFor && (
                <ProfileLookFor
                  data={data.user.dating.lookFor}
                  gender={data.user.gender}
                  lexemes={data.ui.glossary.datingProfile.lookFor}
                  description={
                    lgbtModified ? (
                      <FormattedMessage id="search.for" defaultMessage="Ищу" />
                    ) : (
                      <FormattedMessage
                        id="look.for.foreign"
                        defaultMessage="Интересует"
                      />
                    )
                  }
                />
              )}
              {data.user.dating.lookForAge && (
                <ProfileLookForAge data={data.user.dating.lookForAge} />
              )}
              {data.user.dating.datingGoals &&
                hasSectionGoal(
                  data.user.dating.lookFor,
                  data.user.gender,
                  lgbtRestriction
                ) && (
                  <ProfileSearchGoals
                    data={data.user.dating.datingGoals}
                    lexemes={data.ui.glossary.datingProfile.datingGoals}
                  />
                )}
            </WrapperGroup>
            {mobileGoalPhoto && (
              <MobilePhotosIndex
                hasIncognito={data.user.incognito}
                photo={mobileGoalPhoto}
              />
            )}
            {foreignInterests.length > 0 && (
              <Interests
                data={foreignInterests}
                dataCurrentUser={currentUserInterestList}
                count={foreignInterestsCount}
              />
            )}
            {mobileInterestsPhoto && (
              <MobilePhotosIndex
                hasIncognito={data.user.incognito}
                photo={mobileInterestsPhoto}
              />
            )}
            <WrapperGroup>
              {data.user.dating.materialStatus && (
                <ProfileMaterialStatus
                  data={data.user.dating.materialStatus}
                  lexemes={data.ui.glossary.datingProfile.materialStatus}
                />
              )}
              {data.user.dating.homeStatus && (
                <ProfileAccommodation
                  data={data.user.dating.homeStatus}
                  lexemes={data.ui.glossary.datingProfile.homeStatus}
                />
              )}
              {data.user.dating.children && (
                <ProfileChildren
                  data={data.user.dating.children}
                  lexemes={data.ui.glossary.datingProfile.children}
                />
              )}
              {data.user.dating.education && (
                <ProfileEducation
                  data={data.user.dating.education}
                  lexemes={data.ui.glossary.datingProfile.education}
                />
              )}
              {data.user.dating.knownLanguages && (
                <ProfileLanguage
                  data={data.user.dating.knownLanguages}
                  lexemes={data.ui.glossary.datingProfile.knownLanguages}
                />
              )}
              {data.user.dating.smoking && (
                <ProfileSmoking
                  data={data.user.dating.smoking}
                  lexemes={data.ui.glossary.datingProfile.smoking}
                />
              )}
              {data.user.dating.alcohol && (
                <ProfileAlcohol
                  data={data.user.dating.alcohol}
                  lexemes={data.ui.glossary.datingProfile.alcohol}
                />
              )}
            </WrapperGroup>
            {authorized && <PromoServiceLoadable />}
            {mobileSocialPhoto && (
              <MobilePhotosIndex
                hasIncognito={data.user.incognito}
                photo={mobileSocialPhoto}
              />
            )}
            {data.user.travelAtlas.visitedCountries.visitedCountries.nodes
              .length > 0 && (
              <Countries
                data={
                  data.user.travelAtlas.visitedCountries.visitedCountries.nodes
                }
                currentUserData={
                  data.my?.travelAtlas.visitedCountries.visitedCountries.nodes!
                }
              />
            )}
            <WrapperGroup>
              {data.user.dating.appearance && (
                <ProfileAppearance
                  data={data.user.dating.appearance}
                  lexemes={data.ui.glossary.datingProfile.appearance}
                />
              )}
              {data.user.dating.constitution && (
                <ProfileConstitution
                  data={data.user.dating.constitution}
                  lexemes={data.ui.glossary.datingProfile.constitution}
                />
              )}
              {data.user.dating.height && (
                <ProfileHeight data={data.user.dating.height} />
              )}
              {data.user.dating.weight && (
                <ProfileWeight data={data.user.dating.weight} />
              )}
              {!lgbtModified &&
                !lgbtRestriction &&
                !lgbtHidden &&
                data.user.dating.orientation && (
                  <ProfileOrientation
                    data={data.user.dating.orientation}
                    lexemes={data.ui.glossary.datingProfile.orientation}
                  />
                )}
              {authorized && <HoroscopeSignLoadable />}
            </WrapperGroup>
            <MobilePhotosIndex
              hasIncognito={data.user.incognito}
              photos={mobilePhotos}
            />
            {authorized && (
              <WrapperDesktop>
                <Sharing />
              </WrapperDesktop>
            )}
          </div>
          {authorized && (
            <StickyWrapper $hide={hideButton}>
              <InteractiveButtonContext.Provider
                value={{
                  backgroundColor: Colors.white,
                  id: `button_sticky_${data.user.id}`,
                }}
              >
                <InteractiveButtons
                  isWinked={data.user.relations.winkedByMe!}
                  isFavourite={data.user.relations.contact?.favorite}
                  place="mobile-sticky"
                />
              </InteractiveButtonContext.Provider>
            </StickyWrapper>
          )}
          {authorized && (
            <WrapperMobile>
              <Sharing />
            </WrapperMobile>
          )}
        </Wrapper>
        {!authorized && <ForeignProfileBreadCrumbs />}
        <Route
          path={searchPathFullUrl}
          component={SearchProfile}
          exact={false}
        />
      </ProfileContext.Provider>
    )
  }

  return null
}
